import Events, { EventTypes } from '../../lib/events';
import Logger from '../../lib/logger';
import GamAdSlot from '../../lib/slots/gam_ad_slot';
import { createInitialAdSuppressionState, applyDisable, isSuppressed } from '../../lib/ad_suppression';

/**
 * Public API for disabling ad delivery: app.disableAds(reason, options).
 *
 * Tracks a per-instance { all, video, display } suppression state (see ad_suppression.js) and, when
 * display ads become suppressed, enforces a display gate in Slot.canBeDisplayed()/render(): display
 * (GAM) slots short-circuit, which blocks future and pending requests — including later registerSlot
 * calls, observe re-arms, in-flight renders awaiting the gate/bids, and refresh (refresh() re-enters
 * inserted() -> canBeDisplayed()). On disable it also sweeps each suppressed slot and calls the
 * slot's disableAd() (see GamAdSlot#disableAd), which cancels its queued auto-refresh (so a pending
 * refresh timer can't tear the creative down before that gate is reached) and clears its
 * currently-rendered creative (so a live ad is blanked out rather than left on the page), holding
 * the current rendered height first so the emptied div doesn't collapse and shift the page (CLS).
 *
 * disableAds() clears the ad only — it does NOT destroy the slot or remove its DOM. The wrapper/div
 * stays on the page (just emptied); the request-time gate keeps the now-empty slot from re-requesting.
 *
 * Video is tracked only — Connatix/Reverb player slots are left running for now.
 *
 * Only disableAds() and adsDisabledFor() are exposed on the instance; the state accessor is a
 * module-private helper.
 */

/**
 * Lazily create and return an instance's ad-suppression state.
 * @param {ConcertAds} app
 * @returns {import('../../lib/ad_suppression').AdSuppressionState}
 */
function suppressionState(app) {
  if (!app.adSuppression) {
    app.adSuppression = createInitialAdSuppressionState();
  }

  return app.adSuppression;
}

export function disableAdsMixin(ConcertAds) {
  /**
   * Disable ad delivery. Idempotent — repeat calls with an already-set flag are no-ops (no event,
   * no re-sweep).
   *
   * @param {string} [reason='requested by caller']  Why ads are being disabled (logged).
   * @param {{all?: boolean, video?: boolean, display?: boolean}} [options={all: true}]
   * @returns {undefined}
   */
  ConcertAds.prototype.disableAds = function(reason = 'requested by caller', options = { all: true }) {
    const state = suppressionState(this);
    const changed = applyDisable(state, options);

    if (!changed) {
      Logger.log(`Ad delivery already disabled; ignoring repeat request for: ${reason}`);
      return;
    }

    Logger.log(`Ad delivery disabled (${JSON.stringify(options)}): ${reason}`);

    // Sweep the now-suppressed slots: disableAd() cancels each slot's queued auto-refresh and clears
    // its currently-rendered creative. The request-time gate (Slot.canBeDisplayed()/render()) blocks
    // the *re-request*, but that gate does not stop the destroy that refresh() runs first, nor does
    // it blank an already-rendered ad — disableAd() covers both, leaving the wrapper/div in place.
    this.slots?.forEach(slot => {
      if (this.adsDisabledFor(slot) && typeof slot.disableAd === 'function') {
        slot.disableAd();
      }
    });

    // Fire the event for any plugin that wants to react to the state change.
    Events.emit(EventTypes.adsDisabled, { reason, suppression: { ...state } });
  };

  /**
   * Whether ads are currently disabled — the single source of truth for the suppression decision.
   * Accepts either:
   *   - a slot instance: maps the slot to its ad type (GAM => display; other types are not gated
   *     yet) and returns whether that type is suppressed. The Slot gates (canBeDisplayed/render)
   *     call this, so the slot-to-type mapping lives only here rather than scattered across slot
   *     classes.
   *   - an ad-type string ('all' | 'display' | 'video'): returns that flag directly.
   *
   * @param {Slot|'all'|'video'|'display'} [target='all']
   * @returns {boolean}
   */
  ConcertAds.prototype.adsDisabledFor = function(target = 'all') {
    const state = suppressionState(this);

    if (typeof target === 'string') {
      return isSuppressed(state, target);
    }

    // Slot object: only GAM (display) slots are gated for now. Video players (Connatix/Reverb) and
    // native/commerce units keep running even under `all` — extend the mapping here to gate more.
    if (target instanceof GamAdSlot) {
      return isSuppressed(state, 'display');
    }

    return false;
  };
}
